<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Scrollbar } from 'swiper/modules'
import type { IProps } from './BSPillars.types.js'

import 'swiper/css'
import 'swiper/css/scrollbar'

defineProps<IProps>()

const modules = [Navigation, Scrollbar]
const swiperInstance = ref()

const onSwiper = (swiper: Record<any, any>) => {
  swiperInstance.value = swiper
}

const onClickPrev = () => {
  swiperInstance?.value?.slidePrev()
}

const onClickNext = () => {
  swiperInstance.value?.slideNext()
}

</script>

<template>
  <UiSection
    id="benefit-systems-pillars"
    class="section-pillars"
    color="psychology"
  >
    <UiHeader
      position="center"
      :header="$t(header)"
    />

    <swiper
      ref="sliderRef"
      :modules="modules"
      class="swiper-pillars"
      :loop="true"
      @swiper="onSwiper"
    >
      <swiper-slide
        v-for="(slide, index) in items"
        :key="`slide-${slide.symbol}`"
        class="swiper-slide"
        :class="slide.symbol ? `swiper-slide-${slide.symbol}` : ''"
      >
        <div class="pillars">
          <div class="pillars_col">
            <h4
              v-if="items[index].title"
              class="pillars_title"
            >
              {{ $t(items[index].title) }}
            </h4>

            <div class="pillars_content">
              <h5
                v-if="items[index].label"
                class="pillars_label"
              >
                <span
                  v-if="items[index].symbol"
                  class="pillars_symbol"
                >
                  {{ items[index].symbol }}
                </span>

                {{ $t(items[index].label) }}
              </h5>

              <div
                class="pillars_text"
                v-html="$t(items[index].text)"
              />
            </div>
          </div>

          <div class="pillars_col pillars_col--img">
            <h4
              v-if="items[index].img?.alternative"
              class="pillars_title"
            >
              {{ $t(items[index].img.alternative) }}
            </h4>

            <img
              :style="[
                items[index].img?.width
                  ? `max-width:${items[index].img.width}px`
                  : 'inherit',
                items[index].img?.height
                  ? `max-height:${items[index].img.height}px`
                  : 'inherit'
              ]"
              :alt="items[index].img ? $t(items[index].img.alternative) : ''"
              :src="items[index].img ? $t(items[index].img.url) : ''"
            >
          </div>

          <div
            v-if="items[index].source?.link && items[index].source?.text"
            class="pillars_source"
          >
            <a
              class="pillars_source--link"
              :href="$t(items[index].source.link)"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t(items[index].source.text) }}

              <img
                class="pillars_source--img"
                width="16px"
                height="22px"
                :alt="$t(items[index].source.text)"
                src="/icons/icon-external-link.svg"
              >
            </a>
          </div>
        </div>
      </swiper-slide>
    </swiper>

    <div class="pillars_buttons">
      <div
        class="pillars_button pillars_button--left"
        @click="onClickPrev"
      >
        <img src="/icons/arrow_long.png">
      </div>

      <div
        class="pillars_button pillars_button--right"
        @click="onClickNext"
      >
        <img src="/icons/arrow_long.png">
      </div>
    </div>
  </UiSection>
</template>

<style lang="scss" scoped>
.section-pillars {
  padding: 3.2rem 2rem;
  border-radius: 0;

  :deep(.ui-header) {
    margin-bottom: 2.4rem;
  }

  @include atM {
    padding: 9.6rem;
    border-radius: 1.6rem;

    :deep(.ui-header) {
      padding: 5.6rem;
    }
  }
}

.pillars {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: stretch;
  height: 100%;

  &_col {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    padding: 0;

    &--img {
      align-items: center;
      padding: 0;

      img {
        width: 100%;
        height: auto;
        margin-bottom: auto;
      }
    }
  }

  &_content {
    margin-bottom: auto;
  }

  &_title {
    @include h10;
    margin-bottom: auto;
    padding-bottom: 3.2rem;
    text-align: center;

    .pillars_col--img & {
      @include body-2;
    }
  }

  &_source {
    flex: 100%;
    align-self: flex-end;
    margin-top: 1.6rem;
    text-align: right;

    @include body-4;

    &--link {
      @include body-2;
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    &--img {
      margin-left: 0.8rem
    }
  }

  &_label {
    @include body-1-medium;
    display: flex;
    align-items: center;
    margin-bottom: 3.2rem;
    color: $blue-400;

    .swiper-slide-e & {
      color: $color-environmental
    }

    .swiper-slide-s & {
      color: $color-social
    }

    .swiper-slide-g & {
      color: $color-governance
    }
  }

  &_symbol {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 4.8rem;
    height: 4.8rem;
    margin-right: 1.6rem;
    text-transform: capitalize;
    background-color: $white;
    border-radius: 50%;
  }

  &_text {
    @include body-2-medium;
    margin-bottom: 2.4rem;
  }

  &_buttons {
    position: relative;
    display: flex;
    width: 100%;
    padding: 0;
  }

  &_button {
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 4.8rem;
    height: 4.8rem;
    background-color: $white;
    border: 1px solid $gray-300;
    border-radius: 50%;
    cursor: pointer;

    &--right {
      margin-left: 1.2rem;
    }

    &--left {
      transform: rotate(180deg);
    }
  }

  @include atM {
    flex-direction: row;

    &_col {
      padding: 0 11.2rem 0 6.4rem;
      
      &--img {
        padding: 0 11.2rem 0 0;
      }
    }

    &_title {
      padding-bottom: 2rem;

      .pillars_col--img & {
        @include h10;
      }
    }

    &_source {
      @include body-2-medium;
      align-self: center;
      text-align: right;

      &--link {
        @include body-2;
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }
    }

    &_label {
      @include h9;
    }

    &_text {
      @include h9;
    }

    &_buttons {
      height: 0;
      padding: 0 11.2rem 0 6.4rem;
    }
  }

  @include atL {
   &_col {
      &--img {
        padding: 0 11.2rem 0 6.4rem;
      }
    }
  }
}
</style>
